import { z } from 'zod';

/*
 * We use Zod to be able to define the schema. This is then used to validate and map the env variables
 * sourced from window.env during run time and throwing an ENV ERROR in case the sourced variables
 * don't match the zod schema. The retrieved env is then typed as 'EnvironmentVariables' for convenience.
 * How to use this:
 * In order to add a variable, simple add it to 'environmentVariablesSchema' Zod schema object defining the schema.
 * Then source the variable from window.env in 'envVariables' const within 'getEnvironmentVariables' function
 * for it to be validated.
 */

export const environmentVariablesSchema = z.object({
  APOLLO_URL: z.string(),
  CONNECT_URL: z.string(),
  SUPPORT_FORM_URL: z.string().default('https://share.hsforms.com/12cGsN7W4Q8GFzDg0LxkZSg2e7o8'),
  SUPPORT_GUIDE_URL: z.string().default('https://support.doshii.com/'),
  PRODUCTBOARD_PORTAL_URL: z.string().default('https://portal.productboard.com/brxcizuig4cocphxrh3sep8v'),
  AUTH0_DOMAIN: z.string(),
  AUTH0_CLIENT_ID: z.string(),
  REACT_APP_DOSHII_URL: z.string(),
  REACT_APP_GTM_ID: z.string(),
  REACT_APP_AMPLITUDE_KEY: z.string(),
  ENABLE_AMPLITUDE_SESSION_REPLAY: z.boolean().default(false),
  ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES: z.boolean().default(false),
  ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP: z.boolean().default(false),
  DOORDASH_ID: z.number(),
  MENULOG_ID: z.number(),
  SKIP_ID: z.number(),
  NO_CATEGORY_DESCRIPTION_APP_IDS: z.array(z.number()),
  FEATURED_APPS: z.array(z.string()),
  MAX_IMAGE_SIZE_MB: z.number().default(2),
  MIN_IMAGE_WIDTH_PX: z.number().default(320),
  MIN_IMAGE_HEIGHT_PX: z.number().default(320),
  DOORDASH_MIN_IMAGE_WIDTH_PX: z.number().default(1400),
  DOORDASH_MIN_IMAGE_HEIGHT_PX: z.number().default(800),
  MAX_IMAGE_WIDTH_PX: z.number().default(6000),
  MAX_IMAGE_HEIGHT_PX: z.number().default(6000),
  RESIZE_MAX_IMAGE_WIDTH_PX: z.number().default(1500),
  RESIZE_MAX_IMAGE_HEIGHT_PX: z.number().default(1500),
  BUSY_MODE_WHITELISTED_APP_IDS: z.array(z.number()),
  AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS: z.array(z.number()),
  SURCHARGE_WHITELISTED_APP_IDS: z.array(z.number()),
  OPERATING_HOURS_WHITELISTED_APP_IDS: z.array(z.number()),
  CLOSED_DATES_WHITELISTED_APP_IDS: z.array(z.number()),
  HUBSPOT_TRACKING_ID: z.string()
});

export type EnvironmentVariables = z.infer<typeof environmentVariablesSchema>;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    env: any;
  }
}

let envVariables: EnvironmentVariables;

export const createEnvironmentVariables = (): EnvironmentVariables => {
  const envs = {
    APOLLO_URL: window.env.REACT_APP_APOLLO_URL,
    CONNECT_URL: window.env.REACT_APP_CONNECT_URL,
    AUTH0_DOMAIN: window.env.REACT_APP_AUTH0_DOMAIN,
    AUTH0_CLIENT_ID: window.env.REACT_APP_AUTH0_CLIENT_ID,
    REACT_APP_DOSHII_URL: window.env.REACT_APP_DOSHII_URL,
    REACT_APP_GTM_ID: window.env.REACT_APP_GTM_ID,
    REACT_APP_AMPLITUDE_KEY: window.env.REACT_APP_AMPLITUDE_KEY,
    ENABLE_AMPLITUDE_SESSION_REPLAY: window.env.REACT_APP_AMPLITUDE_SESSION_REPLAY === 'enabled',
    ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES:
      window.env.REACT_APP_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES === 'enabled',
    ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP:
      window.env.REACT_APP_OPERATING_HOURS_FOR_VENUES_WITH_SKIP === 'enabled',
    DOORDASH_ID: window.env.REACT_APP_DOORDASH_ID ? parseInt(window.env.REACT_APP_DOORDASH_ID, 10) : undefined,
    MENULOG_ID: window.env.REACT_APP_MENULOG_ID ? parseInt(window.env.REACT_APP_MENULOG_ID, 10) : undefined,
    SKIP_ID: window.env.REACT_APP_SKIP_ID ? parseInt(window.env.REACT_APP_SKIP_ID, 10) : undefined,
    NO_CATEGORY_DESCRIPTION_APP_IDS: window.env.REACT_APP_NO_CATEGORY_DESCRIPTION_APP_IDS
      ? JSON.parse(window.env.REACT_APP_NO_CATEGORY_DESCRIPTION_APP_IDS)
      : [],
    FEATURED_APPS: window.env.REACT_APP_FEATURED_APPS ? JSON.parse(window.env.REACT_APP_FEATURED_APPS) : [],
    BUSY_MODE_WHITELISTED_APP_IDS: window.env.REACT_APP_BUSY_MODE_WHITELISTED_APP_IDS
      ? JSON.parse(window.env.REACT_APP_BUSY_MODE_WHITELISTED_APP_IDS)
      : [],
    AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS: window.env.REACT_APP_AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS
      ? JSON.parse(window.env.REACT_APP_AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS)
      : [],
    SURCHARGE_WHITELISTED_APP_IDS: window.env.REACT_APP_SURCHARGE_WHITELISTED_APP_IDS
      ? JSON.parse(window.env.REACT_APP_SURCHARGE_WHITELISTED_APP_IDS)
      : [],
    OPERATING_HOURS_WHITELISTED_APP_IDS: window.env.REACT_APP_OPERATING_HOURS_WHITELISTED_APP_IDS
      ? JSON.parse(window.env.REACT_APP_OPERATING_HOURS_WHITELISTED_APP_IDS)
      : [],
    CLOSED_DATES_WHITELISTED_APP_IDS: window.env.REACT_APP_CLOSED_DATES_WHITELISTED_APP_IDS
      ? JSON.parse(window.env.REACT_APP_CLOSED_DATES_WHITELISTED_APP_IDS)
      : [],
    HUBSPOT_TRACKING_ID: window.env.REACT_APP_HUBSPOT_TRACKING_ID
  };
  try {
    envVariables = environmentVariablesSchema.parse(envs);
  } catch (err) {
    throw new Error('Environment variable is not correct!');
  }
  return envVariables;
};

export const getEnvironmentVariables = (): EnvironmentVariables => {
  if (!envVariables) {
    createEnvironmentVariables();
  }
  return envVariables;
};
